/* eslint-disable @typescript-eslint/no-extra-semi */
import { IonCard, IonIcon, IonSpinner } from '@ionic/react'
import { Language } from '@vae/server/dist/client'
import { LocalTimeObject } from '@vae/server/src/interfaces/consumptionData.interface'
import { Chart as ChartJS, registerables } from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { chevronForward, statsChart } from 'ionicons/icons'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Chart } from 'react-chartjs-2'
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { AppContext } from '../../../../contexts/AppContext'
import { UserContext } from '../../../../contexts/UserContext'
import useUser from '../../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../../contexts/objectContext/ObjectContext'
import usePreference from '../../../../utils/preferences/usePreference'
import OurProductsCard from '../../../home/components/OurProductsCard'
import { TimespanOption } from '../../chartUtils/hooks/useChartTime'
import ChartTimeSwitch from '../../components/ChartTimeSwitch'
import QuarterHourlyToggle from '../priceChart/QuarterHourlyToggle'
import ConsumptionChartBanners from './components/ConsumptionChartBanners'
import ConsumptionChartButtons from './components/ConsumptionChartButtons'
import ConsumptionChartScroller from './components/ConsumptionChartScroller'
import ConsumptionTooltip from './components/ConsumptionTooltip'
import getConsumptionTooltipElements from './components/getConsumptionTooltipElements'
import { useChartDatasets } from './hooks/useChartDatasets'
import { useChartTimeConsumptionChart } from './hooks/useChartTimeConsumptionChart'
import { useConsumptionChartData } from './hooks/useConsumptionChartData'
import { getConsumptionChartOptions } from './options/consumptionChartOptions'
import { ChartModes } from './types/types'
import { getShortMonthString, getWeekString } from './utils/consumptionDataParser'

ChartJS.register(...registerables, annotationPlugin)

dayjs.extend(isoWeek)

interface ConsumptionChartProps {
	anchorRef: React.RefObject<HTMLDivElement>
}

const ConsumptionChart: React.FC<ConsumptionChartProps> = ({ anchorRef }) => {
	const { isDarkMode } = useContext(AppContext)
	const { isAnonymousUser, logOut, isUserContextLoading } = useContext(UserContext)
	const object = useContext(ObjectContext)

	const { user } = useUser()
	const { t } = useTranslation('pages_electricity')
	const history = useHistory()
	const time = useChartTimeConsumptionChart()
	const [useQuarterHourlyResolution, setUseQuarterHourlyResolution] = usePreference(
		'preferredChartModeConsumptionChart',
		'hour'
	)

	const {
		data: charts,
		isLoading,
		refetch,
	} = useConsumptionChartData({
		objectId: object.id,
		from: time.startTime.toISOString(),
		to: time.endTime.toISOString(),
		grouping: time.activeTimespan,
		useQuarterHourlyPrices:
			useQuarterHourlyResolution === 'quarter' && time.activeTimespan === 'day',
	})

	const [useBarGraph, setUseBarGraph] = useState(true)
	const [activeElement, setActiveElement] = useState<number>(0)
	const [chartModes, setChartModes] = useState<ChartModes>({
		exchangePrices: true,
		production: false,
		transferPrices: false,
	})

	useEffect(() => {
		if (!isAnonymousUser && !isUserContextLoading && object.id !== 0) refetch()
	}, [
		object.electricityTaxIncluded,
		object.electricityTransferPriceContractType,
		object.electricityTransferPriceContractDayPrice,
		object.electricityTransferPriceContractNightPrice,
		object.electricityTransferPricePerKwh,
		isDarkMode,
		useQuarterHourlyResolution,
	])

	const barRef = useRef<ChartJSOrUndefined<'bar' | 'line', number[], string>>(null)

	const handleChartClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
		if (barRef.current) {
			const index = barRef.current.scales.x.getValueForPixel(event.nativeEvent.offsetX)
			if (index !== undefined && chartDatasets.some(chart => chart.data[index] !== undefined)) {
				const validIndex = Math.max(0, index)
				setActiveElement(validIndex)
			}
		}
	}

	const chartDatasets = useChartDatasets({
		chartModes,
		charts,
		isLoading,
		isDarkMode,
		useQuarterHourlyResolution: useQuarterHourlyResolution === 'quarter',
		useBarGraph,
	})

	const tooltipData = useMemo(
		() =>
			charts
				? getConsumptionTooltipElements(
						charts,
						activeElement,
						chartModes,
						object,
						t,
						time.activeTimespan,
						isDarkMode
					)
				: [],
		[charts, activeElement, time.activeTimespan, chartModes, object, t, isDarkMode]
	)

	const getChartLabel = (
		localTime: LocalTimeObject,
		timespan: 'day' | 'week' | 'month' | 'year' | 'hour',
		language: Language
	): string => {
		switch (timespan) {
			case 'hour':
			case 'day':
				return `${localTime.hour}:${localTime.minute.toString().padStart(2, '0')}`
			case 'week':
				return getWeekString(dayjs.unix(localTime.unix).add(6, 'hours').isoWeekday(), language)
			case 'month':
				return `${localTime.day}`
			case 'year':
				return getShortMonthString(dayjs.unix(localTime.unix).add(6, 'hours').month() + 1, language)
		}
	}

	const chartLabels = useMemo(() => {
		if (charts && charts.consumption && charts.consumption.groupedData) {
			return charts?.consumption.groupedData.map(x =>
				getChartLabel(x.localTime, time.activeTimespan, user?.language || 'fi')
			)
		}
		return []
	}, [charts])

	const options = useMemo(() => {
		return getConsumptionChartOptions({
			activeElement,
			chartModes,
			xLabels: chartLabels,
			isDarkMode,
			onHover: index => {
				if (index !== activeElement) {
					setActiveElement(index)
				}
			},
		})
	}, [
		activeElement,
		chartModes,
		chartLabels,
		isDarkMode,
		charts?.consumption.groupedData,
		time.activeTimespan,
		user?.language,
	])

	const chartData = useMemo(() => {
		const filteredDatasets = chartDatasets
			.filter(chart => {
				if (
					chart.yAxisID === 'transferPrices' &&
					chartModes.exchangePrices &&
					chartModes.transferPrices
				) {
					return false
				}
				if (chart.yAxisID === 'exchangePrices' && !chartModes.exchangePrices) {
					return false
				}
				if (
					chart.yAxisID === 'exchangePriceWithTransferPrice' &&
					(!chartModes.exchangePrices || !chartModes.transferPrices)
				) {
					return false
				}
				return true
			})
			.map(dataset => {
				if (dataset.type === 'line') {
					return {
						...dataset,
						animation: { duration: 0 },
					}
				}
				return dataset
			})

		return {
			labels: chartLabels,
			datasets: filteredDatasets,
		}
	}, [chartLabels, chartDatasets, chartModes.exchangePrices, chartModes.transferPrices])

	const onRegister = () => {
		logOut()
		history.push('/register')
	}
	return (
		<div className='m-auto flex flex-col justify-center gap-md w-full'>
			<ConsumptionChartScroller
				time={time}
				tooltipLoading={isLoading}
				chartData={charts ?? null}
				chartModes={chartModes}
			/>

			<div className='grid grid-cols-1 2xl:grid-cols-2 gap-4 items-start'>
				<IonCard
					id='chart'
					className='w-full rounded-b-[16px] bg-white dark:bg-[#0d2d30] shadow-card'
				>
					{isAnonymousUser ? (
						<div
							onClick={onRegister}
							className='flex p-4 items-center justify-center text-center cursor-pointer hover:bg-gray-300'
						>
							{t('authenticate')}
						</div>
					) : (
						<>
							{charts && (
								<ConsumptionTooltip
									activeElement={activeElement}
									chartData={charts}
									isLoading={isLoading}
									labels={chartData.labels}
									setActiveElement={setActiveElement}
									tooltipData={tooltipData}
									language={user?.language || 'fi'}
									timespan={time.activeTimespan as TimespanOption}
								/>
							)}
						</>
					)}
					<QuarterHourlyToggle
						showQuarterHourly={useQuarterHourlyResolution === 'quarter'}
						onChangeQuarterHourly={(value: boolean) =>
							setUseQuarterHourlyResolution(value ? 'quarter' : 'hour')
						}
						showBarGraph={useBarGraph}
						onChangeBarGraph={setUseBarGraph}
						infoText={t('quarterHourlyToggle.consumptionInfo')}
					/>
					<div
						className={`canvas-container border bg-white border-[#d7d7d7] dark:border-brand-secondary-200-dark dark:bg-[#063a3e]${isLoading ? ' loading' : ''}`}
						ref={anchorRef}
					>
						{isLoading ? (
							<IonSpinner />
						) : (
							<Chart
								ref={barRef}
								type='bar'
								options={options}
								data={chartData}
								onClick={handleChartClick}
							/>
						)}
						{isLoading && <IonSpinner />}
						{!object.id && !object.loading && !isLoading ? (
							<div className='no-data'>{t('userHasNoObject')}</div>
						) : (
							!charts?.consumption?.groupedData &&
							!isLoading && <div className='no-data'>{t('eiDataa')}</div>
						)}
					</div>

					<ChartTimeSwitch time={time} />

					<ConsumptionChartBanners
						mode={'consumption'}
						panelLinked={user?.panelLinked || false}
						productionAuthorized={
							Boolean(object.synerallProductionId) || Boolean(object.productionGsrn)
						}
					/>

					<div className='block 2xl:hidden'>
						<ConsumptionChartButtons
							panelLinked={user?.panelLinked || false}
							chartModes={chartModes}
							setChartModes={setChartModes}
							hasTransferPricesSettings={Boolean(object.electricityTransferPriceContractType)}
						/>
					</div>
				</IonCard>
				<div className='flex flex-col gap-4'>
					{!object?.synerallContractId && <OurProductsCard />}

					<IonCard
						routerLink='/devices'
						className='flex items-center justify-between bg-white dark:bg-[#0d2d30] shadow-card hover'
						style={{ padding: '1.4rem' }}
					>
						<div className='flex flex-row items-center justify-between'>
							<div className='flex flex-row items-center gap-2'>
								<IonIcon
									className='text-brand-primary-100-light dark:text-brand-primary-100-dark'
									style={{ fontSize: '1.5em' }}
									icon={statsChart}
								/>
								<div className='title text text-text-dark dark:text-white'>
									<div className='elementName'>{t('deviceConsumptionInfo')}</div>
								</div>
							</div>

							<IonIcon
								className='text-text-grey-200 dark:text-text-grey-100'
								icon={chevronForward}
							/>
						</div>
					</IonCard>

					<div className='hidden 2xl:block'>
						<ConsumptionChartButtons
							panelLinked={user?.panelLinked || false}
							chartModes={chartModes}
							setChartModes={setChartModes}
							hasTransferPricesSettings={Boolean(object.electricityTransferPriceContractType)}
							showTitle
						/>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ConsumptionChart
